import { useEffect, useRef } from 'react';

import {
  Animated,
  Easing,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import CloseButton from 'components/close-button';
import type { CloseButtonProps } from 'components/close-button/types';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

/**
 * Which side of the bubble the arrow sits on, and therefore where the bubble sits
 * relative to its anchor:
 * - `top`    - bubble below the anchor, arrow points up
 * - `bottom` - bubble above the anchor, arrow points down
 * - `left`   - bubble right of the anchor, arrow points left
 * - `right`  - bubble left of the anchor, arrow points right
 */
export type CoachmarkPlacement = 'top' | 'bottom' | 'left' | 'right';

/** Where the arrow sits along its edge. */
export type CoachmarkArrowAlign = 'start' | 'center' | 'end';

export type CoachmarkDismissButtonPosition = 'inline' | 'top-right';

export type CoachmarkProps = {
  /** Optional resolved heading text. Callers own translation. */
  title?: string;
  /** Resolved supporting text. Callers own translation. */
  body: string;
  onDismiss: () => void;
  /** Arrow side / bubble position relative to the anchor. Defaults to `bottom`. */
  placement?: CoachmarkPlacement;
  /** Arrow position along its edge. Defaults to `center`. */
  arrowAlign?: CoachmarkArrowAlign;
  /**
   * Positioning belongs to the caller: the coachmark is absolutely positioned against
   * whichever element it points at, so anchoring is layout the caller controls. `placement`
   * sets a sensible default anchor (e.g. `bottom` -> `bottom: '100%'`); this style is merged
   * on top so a caller can offset or override it.
   */
  style?: StyleProp<ViewStyle>;
  /** Optional bubble override for callers with experiment-specific spacing needs. */
  bubbleStyle?: StyleProp<ViewStyle>;
  /** Optional body copy override for callers with experiment-specific typography needs. */
  bodyStyle?: StyleProp<TextStyle>;
  /** Optional pointer override for callers that need to align the arrow to a trigger. */
  pointerStyle?: StyleProp<ViewStyle>;
  /** Optional close button container override for experiment-specific placement. */
  dismissButtonStyle?: StyleProp<ViewStyle>;
  /** Optional close button icon override for experiment-specific sizing. */
  dismissButtonIconProps?: CloseButtonProps['_svg'];
  /** Optional close button padding override for experiment-specific sizing. */
  dismissButtonPadding?: CloseButtonProps['padding'];
  /** Optional close button touch target size. Defaults to the shared coachmark size. */
  dismissButtonSize?: number;
  /** Optional close button placement. Defaults to inline with the copy. */
  dismissButtonPosition?: CoachmarkDismissButtonPosition;
  /** Root testID; the dismiss control derives from it as `${testID}-dismiss`. */
  testID?: string;
};

const POINTER_SIZE = 8;
const POINTER_BASE = POINTER_SIZE + 2;
const CLOSE_BUTTON_SIZE = 28;
const FADE_IN_DURATION_MS = 300;
const FADE_IN_TRANSLATE = 12;

const isVertical = (placement: CoachmarkPlacement) => placement === 'top' || placement === 'bottom';

/** Anchors the container to the opposite side from where the arrow points. */
const containerPlacementStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { top: '100%', left: spacing.$1, right: spacing.$1, marginTop: spacing.$4 },
  bottom: {
    // react-native-web resolves `bottom: '100%'` against the anchor's own height, which
    // pushes the bubble too far up once the anchor is `position: relative` (see the
    // coachmarkAnchor fix in store-locator-with-map). 75% matches the intended overlap on web;
    // native Yoga layout doesn't have this issue, so it keeps the full 100%. The web offset
    // already accounts for the gap, so marginBottom is zeroed there to avoid double-spacing.
    bottom: Platform.OS === 'web' ? '75%' : '100%',
    left: spacing.$1,
    right: spacing.$1,
    marginBottom: Platform.OS === 'web' ? 0 : spacing.$4,
  },
  left: { left: '100%', marginLeft: spacing.$4 },
  right: { right: '100%', marginRight: spacing.$4 },
};

/** Stacks the bubble and arrow along the placement axis, arrow on the anchor-facing side. */
const containerFlexStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { flexDirection: 'column-reverse', alignItems: 'stretch' },
  bottom: { flexDirection: 'column', alignItems: 'stretch' },
  left: { flexDirection: 'row-reverse', alignItems: 'center' },
  right: { flexDirection: 'row', alignItems: 'center' },
};

/** The triangle: a border box with one coloured side pointing toward the anchor. */
const pointerPlacementStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: {
    borderLeftWidth: POINTER_BASE,
    borderRightWidth: POINTER_BASE,
    borderBottomWidth: POINTER_SIZE,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: primitive.$black,
  },
  bottom: {
    borderLeftWidth: POINTER_BASE,
    borderRightWidth: POINTER_BASE,
    borderTopWidth: POINTER_SIZE,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: primitive.$black,
  },
  left: {
    borderTopWidth: POINTER_BASE,
    borderBottomWidth: POINTER_BASE,
    borderRightWidth: POINTER_SIZE,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderRightColor: primitive.$black,
  },
  right: {
    borderTopWidth: POINTER_BASE,
    borderBottomWidth: POINTER_BASE,
    borderLeftWidth: POINTER_SIZE,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: primitive.$black,
  },
};

/** Pulls the bubble a pixel toward the arrow to close the Android sub-pixel seam. */
const bubbleSeamOverlapStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { marginTop: -1 },
  bottom: { marginBottom: -1 },
  left: { marginLeft: -1 },
  right: { marginRight: -1 },
};

const arrowAlignToFlex: Record<CoachmarkArrowAlign, ViewStyle['alignSelf']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/**
 * Non-blocking coachmark bubble with a directional pointer and a dismiss control.
 *
 * Presentation only: it holds no eligibility, analytics, or session state; the caller
 * decides when to render it and what dismiss does.
 *
 * `placement` chooses which side the arrow points and where the bubble sits relative to its
 * anchor; `arrowAlign` slides the arrow along that edge. Only the X captures presses; the
 * bubble is `box-none` and the text/pointer are `none`, so controls underneath stay usable.
 */
export const Coachmark = ({
  title,
  body,
  onDismiss,
  placement = 'bottom',
  arrowAlign = 'center',
  style,
  bubbleStyle,
  bodyStyle,
  pointerStyle,
  dismissButtonIconProps,
  dismissButtonPadding,
  dismissButtonPosition = 'inline',
  dismissButtonSize = CLOSE_BUTTON_SIZE,
  dismissButtonStyle,
  testID = 'coachmark',
}: CoachmarkProps) => {
  const coachmarkFadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const fadeInAnimation = Animated.timing(coachmarkFadeAnim, {
      toValue: 1,
      duration: FADE_IN_DURATION_MS,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    });

    fadeInAnimation.start();

    return () => {
      fadeInAnimation.stop();
    };
  }, [coachmarkFadeAnim]);

  const vertical = isVertical(placement);
  const enterFrom = placement === 'bottom' || placement === 'right' ? -1 : 1;
  const slide = coachmarkFadeAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [enterFrom * FADE_IN_TRANSLATE, 0],
  });
  const transform = vertical ? [{ translateY: slide }] : [{ translateX: slide }];

  const pointerAlignStyle: ViewStyle = { alignSelf: arrowAlignToFlex[arrowAlign] };
  const isDismissButtonTopRight = dismissButtonPosition === 'top-right';
  const dismissButtonBaseStyle = isDismissButtonTopRight
    ? styles.coachmarkCloseButtonTopRight
    : styles.coachmarkCloseButton;

  const dismissButton = (
    <CloseButton
      onPress={onDismiss}
      skipRotateAnimation
      skipPressAnimation={isDismissButtonTopRight}
      inverted
      noBackground
      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
      _svg={{ size: '$6', color: primitive.$white, ...dismissButtonIconProps }}
      padding={dismissButtonPadding}
      width={dismissButtonSize}
      height={dismissButtonSize}
      marginRight={isDismissButtonTopRight ? spacing.$0 : spacing.$1}
      style={[dismissButtonBaseStyle, dismissButtonStyle]}
      accessibilityLabel={t('close')}
      testID={`${testID}-dismiss`}
    />
  );

  return (
    <Animated.View
      style={[
        styles.coachmarkContainer,
        containerFlexStyle[placement],
        containerPlacementStyle[placement],
        style,
        { opacity: coachmarkFadeAnim, transform },
      ]}
      pointerEvents="box-none"
      testID={testID}
    >
      <View
        style={[styles.coachmarkBubble, bubbleSeamOverlapStyle[placement], bubbleStyle]}
        pointerEvents="box-none"
      >
        {isDismissButtonTopRight && dismissButton}
        {title ? (
          <>
            <View style={styles.coachmarkTitleRow} pointerEvents="box-none">
              <Typography style={styles.title} pointerEvents="none">
                {title}
              </Typography>
              {!isDismissButtonTopRight && dismissButton}
            </View>
            <Typography style={[styles.body, bodyStyle]} pointerEvents="none">
              {body}
            </Typography>
          </>
        ) : (
          <View style={styles.coachmarkTitleRow} pointerEvents="box-none">
            <Typography style={[styles.body, styles.bodyOnly, bodyStyle]} pointerEvents="none">
              {body}
            </Typography>
            {!isDismissButtonTopRight && dismissButton}
          </View>
        )}
      </View>
      <View
        style={[
          styles.coachmarkPointerBase,
          pointerPlacementStyle[placement],
          pointerAlignStyle,
          pointerStyle,
        ]}
        pointerEvents="none"
        testID={`${testID}-pointer`}
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  body: {
    color: primitive.$white,
    fontSize: 12,
    fontWeight: '400',
    fontFamily: primitive.fontFamily.$body,
    lineHeight: 15,
  },
  bodyOnly: {
    flexShrink: 1,
    fontSize: 14,
    lineHeight: 18,
    marginRight: spacing.$2,
  },
  coachmarkBubble: {
    backgroundColor: primitive.$black,
    borderRadius: 8,
    paddingBottom: spacing.$1_5,
    paddingHorizontal: spacing.$3,
    paddingTop: spacing.$0_5,
    position: 'relative',
  },
  coachmarkCloseButton: {
    flexShrink: 0,
    height: CLOSE_BUTTON_SIZE,
    marginRight: spacing.$1,
    width: CLOSE_BUTTON_SIZE,
  },
  coachmarkCloseButtonTopRight: {
    marginRight: spacing.$0,
    position: 'absolute',
    right: spacing.$3,
    top: spacing.$3,
    zIndex: 1,
  },
  coachmarkContainer: {
    elevation: 10,
    position: 'absolute',
    zIndex: 10,
  },
  coachmarkPointerBase: {
    borderStyle: 'solid',
    height: 0,
    width: 0,
  },
  coachmarkTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$2,
    justifyContent: 'space-between',
  },
  title: {
    color: primitive.$white,
    flexShrink: 1,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 18,
  },
});
